<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import GenericDialog from '@/components/dialog/GenericDialog.vue'
import SpeckleLoginNoticeDialog from '@/components/dialog/SpeckleLoginNoticeDialog.vue'
import ProgressBar from '@/components/shared/ProgressBar.vue'
import { useGIDataSpeckleImport } from '@/composables/useGIDataSpeckleImport'
import { useOpengroundImportProgress } from '@/composables/useOpengroundImportProgress'
import { useOpengroundUIStore } from '@/stores/opengroundUI'
import { useSpeckleSsoStore } from '@/stores/speckleSso'
import {
  OpengroundToSpeckleForm,
  useOpengroundForm,
  useOpengroundStore,
  useSpeckleAuthStore,
  useSpeckleStore
} from '@haskoning/vue-speckle'

const opengroundUIStore = useOpengroundUIStore()
const { importGIData, importProgress } = useGIDataSpeckleImport()
const projectStore = useProjectStore()
const opengroundStore = useOpengroundStore()
const { exportProgress } = storeToRefs(opengroundStore)
const { showDialog } = storeToRefs(opengroundUIStore)
const { currentItem: currentProject } = storeToRefs(projectStore)
const speckleStore = useSpeckleStore()
const speckleAuthStore = useSpeckleAuthStore()
const speckleSsoStore = useSpeckleSsoStore()
const { ssoReauthenticationRequired, canUseSpeckle } = storeToRefs(speckleSsoStore)

function closeDialog() {
  importError.value = ''
  opengroundUIStore.showDialog = false
}

// SpeckleProjectSearch/SpeckleModelSelect skip their own auto-fetch when
// componentsAutoSelectData is true (see VueSpecklePlugin config in main.ts),
// so the host app is responsible for fetching models on project selection.
watch(
  () => speckleStore.currentSelection.project,
  async (project) => {
    if (!project) {
      speckleStore.currentSelection.model = undefined
      return
    }
    await speckleStore.getSpeckleModels()
  }
)

const redirectUrl = computed(() => {
  const currentUrl = new URL(window.location.href)
  currentUrl.hash = ''
  return currentUrl.toString()
})

const opengroundForm = useOpengroundForm()
const alsoImportToLiquefaction = ref(true)
const giDataCollectionName = ref('')
const giDataCollectionColor = ref('#000000')
const isImporting = ref(false)
const importError = ref('')
const isImportDisabled = computed(
  () =>
    isImporting.value ||
    !opengroundForm.isComplete.value ||
    !opengroundStore.opengroundAccessToken ||
    !speckleAuthStore.token ||
    (alsoImportToLiquefaction.value && !giDataCollectionName.value)
)

const {
  currentStage,
  exportPercent,
  importPercent,
  importIndeterminate,
  exportMessage,
  importMessage
} = useOpengroundImportProgress(exportProgress, importProgress)

async function handleImport() {
  if (isImporting.value) {
    return
  }
  isImporting.value = true
  currentStage.value = 1
  importError.value = ''

  try {
    const exportResponse = await opengroundStore.exportFormToSpeckle(opengroundForm)
    if (!exportResponse) {
      return
    }

    if (alsoImportToLiquefaction.value) {
      const speckleToken = speckleAuthStore.token
      const speckleProjectId = speckleStore.currentSelection.project?.id
      const speckleModelId = speckleStore.currentSelection.model?.id
      const projectId = currentProject.value?.id

      if (
        !giDataCollectionName.value ||
        !speckleToken ||
        !speckleProjectId ||
        !speckleModelId ||
        !projectId
      ) {
        return
      }

      currentStage.value = 2
      await importGIData({
        speckle_token: speckleToken,
        speckle_project_id: speckleProjectId,
        speckle_model_id: speckleModelId,
        speckle_version_id: exportResponse.speckle_commit_id,
        project: projectId,
        description: `Imported from Speckle with id ${speckleProjectId}`,
        name: giDataCollectionName.value,
        collection_color: giDataCollectionColor.value
      })
    }

    if (speckleStore.currentSelection.project && speckleStore.currentSelection.model) {
      await speckleStore.getSpeckleVersions()
    }

    closeDialog()
  } catch (error) {
    importError.value = error instanceof Error ? error.message : 'Import failed'
  } finally {
    isImporting.value = false
    currentStage.value = 0
  }
}

async function opengroundLogin() {
  const loginUrl = await opengroundStore.generateLoginUrl(redirectUrl.value)
  if (loginUrl) {
    window.location.href = loginUrl
  }
}
</script>

<template>
  <SpeckleLoginNoticeDialog v-if="!canUseSpeckle" v-model="opengroundUIStore.showDialog">
    <template #default v-if="!ssoReauthenticationRequired">
      <p class="pb-2">
        Before first use of the connection between OpenGround and Liquefaction the user must request
        access to Speckle:
      </p>
      <ul class="pl-8 pb-2">
        <li>
          Speckle - Request access via
          <a
            href="https://royalhaskoningdhv-amc.ivanticloud.com/Modules/SelfService/#serviceCatalog/request/6215C7ADD8CB4705BAD0EDC04E8F9E77"
            target="_blank"
            rel="noopener noreferrer"
          >
            Speckle Manager / Service Catalogue / WeConnect</a
          >
        </li>
      </ul>
      <p class="pb-2">
        For a new project the user will need to request a Speckle project. This request should be
        made by email or teams message to:
      </p>
      <ul class="pl-8 pb-2">
        <li>
          <a href="mailto:jelle.van.der.zon@haskoning.com">jelle.van.der.zon@haskoning.com</a> and
        </li>
        <li>
          <a href="mailto:neethu.ragi.manoj@haskoning.com">neethu.ragi.manoj@haskoning.com</a>
        </li>
      </ul>
      <p class="pb-2">
        Once the user has confirmation that their project has been created on Speckle then
        connection may be used via this button.
      </p>
      <p>If you have completed the actions above, select Continue below.</p>
    </template>
  </SpeckleLoginNoticeDialog>
  <GenericDialog
    :model-value="showDialog"
    v-else-if="!opengroundStore.isLoggedIn"
    @clicked-outside="closeDialog"
  >
    <template #title>Openground Login Required</template>
    <template #default>
      <p class="pb-2">To continue you need to login into Openground first</p>
    </template>
    <template #actions>
      <v-btn variant="text" color="primary" @click="closeDialog"> Cancel </v-btn>
      <v-btn variant="text" color="primary" @click="opengroundLogin"> Continue </v-btn>
    </template>
  </GenericDialog>
  <GenericDialog :model-value="showDialog" v-else @clicked-outside="closeDialog">
    <template #title>OG2Speckle</template>
    <template #default>
      <fieldset
        :disabled="isImporting"
        class="openground-form-fieldset"
        :class="{ 'openground-form-fieldset--disabled': isImporting }"
      >
        <OpengroundToSpeckleForm :form="opengroundForm">
          <template #version-message-label> Speckle Import Message </template>
        </OpengroundToSpeckleForm>
      </fieldset>
      <template v-if="alsoImportToLiquefaction">
        <v-text-field
          v-model="giDataCollectionName"
          label="Name of GI Data Collection"
          color="primary"
          class="mt-2"
          :rules="[(v: string) => !!v || 'Name is required']"
          :disabled="isImporting"
          hide-details="auto"
        ></v-text-field>
      </template>
      <div v-if="isImporting" class="mt-4">
        <ProgressBar :percent="exportPercent" :message="exportMessage" />
        <ProgressBar
          v-if="alsoImportToLiquefaction"
          class="mt-3"
          :percent="importIndeterminate ? null : importPercent"
          :indeterminate="importIndeterminate"
          :message="importMessage"
        />
      </div>
      <v-alert v-if="importError" type="error" variant="flat" class="mt-4">
        {{ importError }}
      </v-alert>
    </template>
    <template #actions>
      <v-btn variant="text" color="primary" @click="closeDialog"> Cancel </v-btn>
      <v-btn
        variant="text"
        color="primary"
        :loading="isImporting"
        :disabled="isImportDisabled"
        @click="handleImport"
      >
        Import
      </v-btn>
    </template>
  </GenericDialog>
</template>

<style scoped>
.openground-form-fieldset {
  border: 0;
  padding: 0;
  margin: 0;
  min-inline-size: auto;
}

.openground-form-fieldset--disabled {
  opacity: 0.6;
  pointer-events: none;
}
</style>
